import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import * as mobxReact from 'mobx-react';
import React, { useRef } from 'react';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { action } from 'mobx';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { IOperatorDetailsDTO } from '../../../../../api/rooms.dao';
import { withViewModel } from '../../../../../infrastructure/components/with-view-model';
import { ISubWindowFormMethods } from '../../manage-event.viewmodel';
import { OperatorsList } from '../../shared/operators-list/operators-list';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { MeetingConnectionsFormVM } from './meeting-connections-form.viewmodel';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { getFormattedTimezoneOffset } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { roleNameMap } from '../../../../../modules/connections/const';
import { OrganizationRole } from '../../../../../modules/connections/types';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../global/form/form-field/form-field';
import OptionsButton from '../../../../global/options-button/options-button';
import { RichTextEditor } from '../../../../global/rich-text-editor';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { ConnectionsList } from '../../shared/connections-list/connections-list.presentation';
import { ParticipantConnectionList } from '../_connection_participants/participant-connection-list';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { OEToggle } from '../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../global/text/text';
import { RecordingsStatus } from '../../../../../api/meetings.dao';
import { getCompleteRoomName } from '../../../../../infrastructure/utils/common.utils';
import { IUserOperatorDTO } from '../../../../../api/operators.dao';
import DefaultButton from '../../../../global/default-button/default-button';
import { ConnectionType, IConnectionDetailsDTO, onChangeConnectionEntity } from '../../../../../api/connections.dao';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { ConnectionVersion, EnablementStatus } from '../../../../../constants/common.constants';
import { applyText, CANCEL_TEXT, cancelText, MORE_BUTTON_TEXT } from '../../../../../constants/common-labels.constants';
import MeetingConnectionsTemplates from './connections-templates/connections-templates';
import {
    EDIT_MEETING_OPTION_TEXT,
    EDIT_REGISTRANTS_TEXT,
    MANAGE_PODIUM_ACCESS_TEXT,
    MEETING_GUIDELINES_TEXT,
    REGISTRANTS_TEXT,
    RUN_MEETING_OPTION_TEXT,
    SEND_CONNECTIONS_EMAILS_OPTION_TEXT
} from './meeting-connections-form.texts';
import { EmailTemplatesListDropdown } from './email-templates-dropdown/email-templates-dropdown';
import { ConnectionGroupsList } from '../../shared/connection-groups-list/connection-groups-list';
import { MEETING_CONNECTIONS_AUTO_SUBSCRIPTION_TEXT, MEETING_CONNECTIONS_AUTOREG_TEXT } from '../connections.texts';
import { meetingConnectionsAutoRegInfoMessage, meetingConnectionsAutoSubscriptionInfoMessage } from '../../../../../constants/connections-labels.constants';
import { InfoPopover } from '../../../../global/info-popover/info-popover';
import classNames from 'classnames';
import { PODIUM_LINK_TEXT } from './podium-form/podium-form.texts';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { MeetingLinksSection } from './meeting-links/meeting-links-section.presentation';

import '../connections.scss';

interface IProps {
    vm: MeetingConnectionsFormVM;
    intl: IntlShape;
}

const MeetingConnectionsFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const formRef = useRef(null);
    const { intl, vm: formVM } = props;
    const formData = formVM.dataVM;
    if (!formData) return;

    const onChangeMeetingConnection = action((
        entity: onChangeConnectionEntity,
        connectionId: number | null,
        changePresenterOrHost?: boolean
    ): void => {
        if (connectionId === null) {
            entity.removeAssignedConnection();
        } else {
            entity.assignConnection(connectionId);
        }
        // If attendee has Presenter or Host role than Unique Joining Link popup doesn't update
        if (changePresenterOrHost && formVM.isUniqueJoiningLinkShown()) {
            formVM.updateUniqueJoiningLinkForm();
        }
    });

    const { startDateTime, endDateTime, room, connectionVersion } = formData.getProps();

    const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);
    const startTime = dateTimeFormatter.dateToFormattedString(startDate, dateTimeFormatter.getLocalTimeFormat());
    const endTime = dateTimeFormatter.dateToFormattedString(endDate, dateTimeFormatter.getLocalTimeFormat());
    const eventTimezone = formVM.getEventTimeZone();

    const startDateInEventTimezone = utcToZonedTime(startDateTime, eventTimezone);
    const endDateInEventTimezone = utcToZonedTime(endDateTime, eventTimezone);

    const dayStringInEventTimezone = dateTimeFormatter.dateToFormattedString(
        startDate,
        DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
        eventTimezone
    );

    const webinarRegistrantsCount =
        formVM.dataVM.getOrganizationsByRole(OrganizationRole.Registrants)
            .reduce((acc, curr) => acc + curr.getEntities().length, 0);

    const presenters = formData.getOrganizationsByRole(OrganizationRole.Presenters);
    const presenterOrganizationList = formData.getOrganizationListByRole(OrganizationRole.Presenters);

    const meetingPresenters = (presenters.length ? ' - ' + presenters.map(p => p.getProps().name).join(', ') : '');
    const meetingTopic = formVM.meetingTopic || formVM.getEventTitleForPassport() || formVM.getEventName();

    const roomAndMeetingOperators = [
        ...formVM.roomOperators,
        ...formVM.meetingOperators as IOperatorDetailsDTO[]
    ];

    const startTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(startDate, dateTimeFormatter.getLocalTimeFormat(), eventTimezone);
    const endTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(endDate, dateTimeFormatter.getLocalTimeFormat(), eventTimezone);
    const timeZoneOffset = getFormattedTimezoneOffset(startDate);
    const isEventTimezoneTimeDifferent = startTimeInEventTimezone !== startTime;
    const localDayString = dateTimeFormatter.dateToFormattedString(startDate, DateFnsFormats.StringifiedFullDayMonthNumericDateYear);
    const knovioReplayConnection = formData.getKnovioReplayConnection();
    const disabledDropDownConnection = !!knovioReplayConnection;
    const showAutoReg = hasPermission(Permission.CONNECTIONS_MANAGEMENT) && formVM.connectionVersion === ConnectionVersion.V2;
    const shouldShowManagePodiumAccess = hasPermission(Permission.MANAGE_PODIUM_ACCESS);
    const shouldShowMeetingLinks = hasPermission(Permission.VIEW_MEETING_LINKS);
    const shouldShowAddMeetingLink = hasPermission(Permission.MANAGE_MEETING_LINKS);

    const meetingConnectionOptions = [
        hasPermission(Permission.SEND_CONNECTIONS_EMAIL) && {
            label: intl.formatMessage(SEND_CONNECTIONS_EMAILS_OPTION_TEXT),
            action: formVM.openEmailTemplatesDropdown,
            setHandlerForClosePopup: (handler: () => void) => {
                formVM.closeOptionsButton = handler;
            },
        },
        hasPermission(Permission.UPDATE_MEETING) && {
            label: intl.formatMessage(EDIT_MEETING_OPTION_TEXT),
            action: formVM.editMeeting,
        },
        hasPermission(Permission.RUN_MEETING) && {
            label: intl.formatMessage(RUN_MEETING_OPTION_TEXT),
            action: formVM.runMeeting,
        }
    ].filter(Boolean);

    const getConnectionsFormBody = (): JSX.Element => {
        const nonEmptyOperators = !!(formVM.roomOperators.length || formVM.meetingOperators.length);
        const nonEmptyRoomAndMeetingOperators = !!(formVM.roomOperators.length && formVM.meetingOperators.length);

        return (
            <form className='oe-form meeting-connections-form'>
                {/* MEETING DATES */}
                <div className='control-form__date'>
                    <span className='control-form__event-day'>
                        {dayStringInEventTimezone}
                    </span>
                    <div className='control-form__event-time'>
                        {startTimeInEventTimezone} - {endTimeInEventTimezone}
                    </div>
                    {!isSameDay(startDate, startDateInEventTimezone) &&
                        (
                            <div className='control-form__local-day' data-testid='local-day'>
                                {localDayString}
                            </div>
                        )}
                    {isEventTimezoneTimeDifferent && (
                        <div className='control-form__local-time' data-testid='local-time'>
                            {startTime} - {endTime} ({timeZoneOffset})
                        </div>
                    )}
                </div>
                {/* MEETING TEMPLATE NAME */}
                {
                    props.vm.shouldShowMeetingTemplateLabel() &&
                    <div className='meeting-template-name'>
                        <span className='meeting-template-name--label'>
                            <FormattedMessage
                                id='oe.meeting.template'
                                defaultMessage='Meeting Template{suffix}'
                                description='label'
                                values={{ suffix: ':' }}
                            />
                        </span>
                        <Text className='meeting-template-name--value' message={props.vm.meetingTemplateName} withEllipsis />
                    </div>
                }
                {
                    props.vm.hasConnectionTemplatesPermissions() &&
                    <MeetingConnectionsTemplates
                        vm={props.vm.connectionTemplatesVM}
                        intl={intl}
                    />
                }
                {/* MEETING RECORDING */}
                <div className='recording__label-wrapper'>
                    <div className={classNames('recordings-icon', { 'recordings-icon-active': props.vm.getMeetingRecordProps().isSelected })} />
                    <Text message={`Record Meeting: ${props.vm.getMeetingRecordProps().isSelected ? RecordingsStatus.Yes : RecordingsStatus.No}`} />
                </div>
                {/* MEETING GUIDELINES */}
                <FormField className='read-only-textarea'>
                    <RichTextEditor
                        label={<FormattedMessage {...MEETING_GUIDELINES_TEXT} />}
                        {...formVM.getMeetingConnectionNotesProps()}
                    />
                </FormField>
                {/* AUTO REG */}
                {showAutoReg &&
                    <FormField className='auto-reg-toggle'>
                        <OEToggle {...props.vm.getAutoRegToggleProps()} />
                        <Text
                            message={intl.formatMessage(MEETING_CONNECTIONS_AUTOREG_TEXT, {
                                suffix: props.vm.getAutoRegToggleProps().isSelected ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            })}
                        />
                        <InfoPopover className='m-left-8px'>
                            <div>{meetingConnectionsAutoRegInfoMessage}</div>
                        </InfoPopover>
                    </FormField>
                }
                {/* AUTO SUBSCRIPTION */}
                {showAutoReg &&
                    <FormField className='auto-subscription-toggle'>
                        <OEToggle {...props.vm.getAutoSubscriptionToggleProps()} />
                        <Text
                            message={intl.formatMessage(MEETING_CONNECTIONS_AUTO_SUBSCRIPTION_TEXT, {
                                suffix: props.vm.getAutoSubscriptionToggleProps().isSelected ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            })}
                        />
                        <InfoPopover className='m-left-8px'>
                            <div>{meetingConnectionsAutoSubscriptionInfoMessage}</div>
                        </InfoPopover>
                    </FormField>
                }
                {/* ROOM NAME */}
                {room &&
                    <FormField label={<FormattedMessage id='oe.input.room' description='Input label' defaultMessage='Room' />}>
                        <input
                            className='oe-form-field__control'
                            value={getCompleteRoomName({ name: room.roomName, label: room.roomLabel })}
                            disabled
                        />
                    </FormField>
                }
                <div className='oe-form__body'>
                    {/* CONNECTION LIST */}
                    <ConnectionsList
                        isMeetingConnectionsForm={true}
                        startDate={startDateInEventTimezone}
                        endDate={endDateInEventTimezone}
                        timeZone={eventTimezone}
                        meetingApiParams={formVM.meetingApiParams}
                        topic={meetingTopic}
                        organization={presenters?.length === 1 && meetingPresenters}
                        connectionTypes={formVM.connectionTypes}
                        connections={formData.getFilteredMeetingConnectionList(ConnectionType.UniqueJoiningLink)}
                        defaultConnectionId={formData.getAssignedConnection()}
                        updateDefaultConnection={formVM.updateDefaultConnectionId}
                        createConnection={formVM.createMeetingConnection}
                        updateConnection={formVM.updateMeetingConnection}
                        deleteConnection={formVM.deleteConnection}
                        intl={intl}
                        event={formVM.event}
                        generalMeetingId={formVM.generalMeetingId}
                        getKnovioConnectionDetails={formVM.getKnovioConnectionDetails}
                        openFrom={OpenFrom.Connections}
                        isKnovioReplayConnectionExist={formData.isKnovioReplayConnectionExist.bind(formData)}
                        handleTroubleshoot={formVM.handleTroubleshoot}
                        showConfigurePassportForm={formVM.showConfigurePassportForm}
                        trackHostJoinClick={formVM.trackHostJoinClick}
                        presenterOrganizationList={presenterOrganizationList}
                        meetingTopic={formVM.meetingTopic}
                        organizationsForSpeakersSync={formVM.getOrganizationsForSpeakersSync()}
                        connectionVersion={formVM.connectionVersion}
                        isMeetingV2={formVM.connectionVersion === ConnectionVersion.V2}
                    />
                    {/* MEETING CONNECTION GROUPS */}
                    {formVM.connectionVersion === ConnectionVersion.V2 &&
                        <ConnectionGroupsList
                            getGroups={formData.getMeetingConnectionGroupList.bind(formData)}
                            getConnections={formData.getFilteredMeetingConnectionList.bind(formData, ConnectionType.UniqueJoiningLink)}
                            contextParams={{ meetingId: formVM.meetingApiParams.meetingId }}
                            createConnectionGroup={formVM.createMeetingConnectionGroup}
                            getDefaultConnectionId={formData.getAssignedConnection.bind(formData)}
                            updateDefaultConnection={formVM.updateDefaultConnectionId}
                            editConnectionGroup={formVM.editMeetingConnectionGroup}
                            deleteConnectionGroup={formVM.deleteMeetingConnectionGroup}
                        />
                    }
                    <div className='attendee-list'>
                        <div className={`attendee-list__label attendee-list__label-operators ${nonEmptyOperators && 'attendee-list__header' || ''}`}>
                            <FormattedMessage id='oe.common.operators' description='Label text' defaultMessage='Operators' />
                        </div>
                        {/* ROOM OPERATORS (READ-ONLY) */}
                        <OperatorsList operators={formVM.roomOperators as IUserOperatorDTO[]} displayOperatorRole classname='operator-list--read-only' />
                        {nonEmptyRoomAndMeetingOperators && <div style={{ width: '100%', borderTop: '1px solid #949494' }} />}
                        {/* MEETING OPERATORS */}
                        <OperatorsList
                            operators={formVM.meetingOperators as IUserOperatorDTO[]}
                            removeOperator={formData.removeOperatorByEmail.bind(formData)}
                            onOperatorRoleChange={formData.onOperatorRoleChange}
                        />
                        <MultiSelectButton
                            className='attendee-list__add-button'
                            items={formVM.operators}
                            disabled={roomAndMeetingOperators}
                            value={roomAndMeetingOperators}
                            keyProperty='email'
                            nameProperty='fullName'
                            text={<FormattedMessage
                                id='oe.operators.add'
                                description='Button text'
                                defaultMessage='{prefix} Add Operators'
                                values={{ prefix: '+' }}
                            />}
                            applyButtonText={intl.formatMessage({ id: 'oe.common.add', description: 'Button text', defaultMessage: 'Add' })}
                            resetButtonText={intl.formatMessage(CANCEL_TEXT)}
                            onChange={formVM.selectOperators.bind(formVM)}
                            filterHandler={formVM.searchOperators.bind(formVM)}
                        />
                    </div>
                    {/* PODIUM 2.0 */}
                    {formVM.isPodiumEnabled &&
                        <FormField label={intl.formatMessage(PODIUM_LINK_TEXT)}>
                            <div className='podium-link'>
                                <input
                                    className='oe-form-field__control'
                                    disabled={true}
                                    value={formVM.podiumLink}
                                />
                                <CopyButton
                                    copyValue={formVM.podiumLink}
                                    className='copy-podium-link-button'
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                />
                            </div>
                        </FormField>
                    }
                    {shouldShowManagePodiumAccess &&
                        <DefaultButton
                            modifier='fat'
                            text={intl.formatMessage(MANAGE_PODIUM_ACCESS_TEXT)}
                            onClick={props.vm.openPodiumPopup}
                            className='manage-podium-access'
                        />
                    }
                    {shouldShowMeetingLinks &&
                        <MeetingLinksSection
                            vm={formVM.meetingLinksVM}
                            connections={formData.getMeetingConnectionList()}
                            connectionGroups={formData.getMeetingConnectionGroupList()}
                            inheritanceConnections={formVM.meetingLinkInheritanceConnections()}
                            inheritanceIds={formVM.meetingLinkInheritanceIds()}
                            canManage={shouldShowAddMeetingLink}
                            isAddActive={formVM.isManageMeetingLinkModalShown()}
                            onAddClick={formVM.openManageMeetingLinkModal}
                            isLinkEditing={formVM.isEditMeetingLinkModalShown}
                            onEditClick={formVM.openEditMeetingLinkModal}
                        />
                    }
                    {/* ROLE-GROUPED ORGANIZATIONS WITH PARTICIPANTS */}
                    {Object.entries(roleNameMap)
                        // eslint-disable-next-line no-unused-vars
                        .filter(([_, role]) => role !== OrganizationRole.Registrants)
                        .map(([name, role]) => {
                            return (
                                <ParticipantConnectionList
                                    key={name}
                                    roleName={name}
                                    role={role}
                                    entity={formData}
                                    attendees={formVM.attendees}
                                    meetingConnections={formData.getMeetingConnectionList()}
                                    filteredMeetingConnections={formData.getFilteredMeetingConnectionList(ConnectionType.UniqueJoiningLink)}
                                    onChangeMeetingConnection={onChangeMeetingConnection}
                                    editConnection={formVM.handleEditConnection}
                                    knovioReplayConnection={knovioReplayConnection}
                                    disabledDropDownConnection={disabledDropDownConnection}
                                    connectionVersion={connectionVersion}
                                    connectionGroups={formData.getMeetingConnectionGroupList()}
                                    handleTrackChildCopyBtn={formVM.trackChildCopyButtonMixPanel}
                                    childConnectionsVM={formVM.childConnectionsVM}
                                />
                            );
                        })
                    }

                    <div className='attendee-list'>
                        <div className='attendee-list__row'>
                            <div className='attendee-list__label w-100'>
                                {webinarRegistrantsCount} <FormattedMessage {...REGISTRANTS_TEXT} />
                            </div>
                        </div>
                        <DefaultButton
                            className='attendee-list__add-button'
                            modifier='slim'
                            isActive={formVM.isUniqueJoiningLinkShown()}
                            text={intl.formatMessage(EDIT_REGISTRANTS_TEXT)}
                            onClick={() => formVM.openUniqueJoiningLinkModal(onChangeMeetingConnection)}
                        />
                    </div>
                </div>
            </form>
        );
    };

    return (
        <CommonPopupBodyFooterLayout
            body={getConnectionsFormBody()}
            footer={(
                <>
                    <EmailTemplatesListDropdown
                        isOpened={formVM.isEmailTemplatesDropdownOpened}
                        selectTemplateFromList={formVM.selectTemplateFromList}
                        emailTemplatesList={formVM.emailTemplates}
                        closeEmailTemplatesDropdown={formVM.closeEmailTemplatesDropdown}
                        loadingEmailTemplates={formVM.isLoadingEmailTemplates}
                        itemRef={formRef}
                        position='left'
                        selectedEmailTemplate={formVM.selectedEmailTemplate}
                        customLeft={-290}
                        customTop={100}
                    />
                    {formVM.isSaving() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.connections.connectionDataIsBeingSaved'
                                description='Preloader message text'
                                defaultMessage='Connection data is being saved, please wait{suffix}'
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span>
                    )}
                    {!formVM.getIsFormDataReady() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.connections.connectionDataIsLoading'
                                description='Preloader message text'
                                defaultMessage='Connection data is loading, please wait{suffix}'
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right' ref={formRef}>
                        <DefaultCloseSubWindowButton
                            text={applyText()}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => formVM.formSubmitAction()}
                            disabled={!formVM.getIsFormDataReady()}
                        />
                        <DefaultCloseSubWindowButton
                            text={cancelText()}
                        />
                        {!!meetingConnectionOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={meetingConnectionOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            )}
        />
    );
}));

interface IPropsConnectionsForm {
    accountId: number;
    event: IEventDetailsDTO;
    saveCompletedCallback: () => Promise<void>;
    meetingId: number;
    subWindowFormMethods: ISubWindowFormMethods;
    closeSubWindow: (meetingId: number) => void;
    editConnection?: (
        meetingId: number,
        connection: IConnectionDetailsDTO,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void
    ) => void;
    openFrom?: OpenFrom;
    onClosePopupHandler?: (callback: () => void) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    closeMeetingConnectionPopUp: () => void;
}

const MeetingConnectionsForm = withViewModel(MeetingConnectionsFormPresentation, async (props: IPropsConnectionsForm) => {
    const {
        accountId,
        event,
        saveCompletedCallback,
        meetingId,
        subWindowFormMethods,
        closeSubWindow,
        editConnection,
        openFrom,
        onClosePopupHandler,
        handleTroubleshoot,
        showConfigurePassportForm,
        trackHostJoinClick,
        closeMeetingConnectionPopUp,
    } = props;

    const vm = new MeetingConnectionsFormVM(
        accountId,
        event,
        saveCompletedCallback,
        meetingId,
        subWindowFormMethods,
        closeSubWindow,
        onClosePopupHandler,
        handleTroubleshoot,
        showConfigurePassportForm,
        trackHostJoinClick,
        closeMeetingConnectionPopUp,
        editConnection,
        openFrom,
    );
    await vm.initialize();
    return vm;
}, props => [props.accountId, props.event, props.meetingId]);


export { MeetingConnectionsFormPresentation, MeetingConnectionsForm };
